import React, { useMemo, useState } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { RiCloseLine } from 'react-icons/ri';
import { FiCreditCard } from 'react-icons/fi';
import { usePayerObligationFiscaleMutation } from 'actions/fiscalite/mutations';
import { useGetComptePaiementQuery } from 'actions/compte_paiement/queries';
import { toastApiErrors } from 'utils/toastApiError';

if (typeof window !== 'undefined') {
  Modal.setAppElement('#root');
}

const formatMontant = (m) => Number(m || 0).toLocaleString('fr-FR');

/**
 * Modal de règlement d'une obligation fiscale.
 * Choix du compte de paiement + date -> POST /fiscalite/obligations/<id>/payer/
 * Le backend crée la sortie trésorerie, débite le compte et génère
 * l'écriture comptable au journal FISCALITE.
 */
const ModalPaiementFiscal = ({ isOpen, onClose, obligation, dossierId }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [comptePaiementId, setComptePaiementId] = useState('');
  const [datePaiement, setDatePaiement] = useState(today);

  const { data: comptesData } = useGetComptePaiementQuery(
    { dossier_id: dossierId },
    { skip: !isOpen }
  );
  const [payerObligation, { isLoading }] = usePayerObligationFiscaleMutation();

  const comptes = useMemo(
    () => (comptesData?.data || []).filter((c) => !c.archived),
    [comptesData]
  );

  const handleConfirm = async () => {
    if (!comptePaiementId) {
      toast.warning('Veuillez sélectionner un compte de paiement.');
      return;
    }
    if (!datePaiement) {
      toast.warning('Veuillez saisir la date de paiement.');
      return;
    }
    try {
      await payerObligation({
        id: obligation.id,
        dossier_id: dossierId,
        compte_paiement_id: Number(comptePaiementId),
        date_paiement: datePaiement,
      }).unwrap();
      toast.success(`Obligation « ${obligation.nom} » réglée avec succès.`);
      setComptePaiementId('');
      setDatePaiement(today);
      onClose();
    } catch (error) {
      toastApiErrors(error);
    }
  };

  if (!obligation) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Règlement fiscal"
      className="Modal fisc-modal fisc-modal-pay"
      overlayClassName="Overlay"
    >
      <div className="fisc-modal-header">
        <div className="fisc-modal-icon">
          <FiCreditCard size={20} />
        </div>
        <div className="fisc-modal-titles">
          <h2>Déclarer &amp; payer l&rsquo;obligation</h2>
          <p>La sortie trésorerie et l&rsquo;écriture comptable sont générées automatiquement</p>
        </div>
        <button
          type="button"
          className="fisc-modal-close"
          onClick={onClose}
          aria-label="Fermer"
        >
          <RiCloseLine size={18} />
        </button>
      </div>

      <div className="fisc-modal-body">
      <div className="fisc-modal-recap">
        <div className="text-muted small">Impôt / Obligation</div>
        <div className="fw-bold">{obligation.nom}</div>
        <div className="d-flex justify-content-between mt-1">
          <span>Code : {obligation.code || ':'}</span>
          <span className="fw-bold">{formatMontant(obligation.montant)} FCFA</span>
        </div>
      </div>

      {Number(obligation.montant) <= 0 && (
        <div className="alert alert-warning py-2" style={{ fontSize: 13 }}>
          Le montant exigible est à 0 FCFA : modifiez l&rsquo;obligation pour le
          renseigner avant le paiement.
        </div>
      )}

      <div className="form-group mt-3">
        <label className="fw-bold small">Compte de paiement débiteur</label>
        <select
          className="form-control"
          value={comptePaiementId}
          onChange={(e) => setComptePaiementId(e.target.value)}
        >
          <option value="">: Sélectionner un compte :</option>
          {comptes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.compte} ({formatMontant(c.solde_comptable ?? c.montant_restant)} FCFA dispo)
            </option>
          ))}
        </select>
      </div>

      <div className="form-group mt-3">
        <label className="fw-bold small">Date de paiement effectif</label>
        <input
          type="date"
          className="form-control"
          value={datePaiement}
          onChange={(e) => setDatePaiement(e.target.value)}
        />
      </div>

      </div>

      <div className="fisc-modal-footer">
        <button className="fisc-btn-cancel" onClick={onClose} disabled={isLoading}>
          Annuler
        </button>
        <button
          className="fisc-btn-primary"
          onClick={handleConfirm}
          disabled={isLoading || Number(obligation.montant) <= 0}
        >
          {isLoading ? 'Paiement...' : 'Confirmer le paiement'}
        </button>
      </div>
    </Modal>
  );
};

export default ModalPaiementFiscal;
